import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '../../../components/ui/tooltip';
import {
  FileText, CheckCircle2, XCircle, ShieldAlert, FileSearch, ArrowRightLeft, Zap, Send, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { conciliacionProveedoresApi } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { SemaforoBadge, EstadoMatchBadge } from './Badges';
import { fmtCLP, fmtDate, fmtFecha, getSemaforo } from '../utils';
import { DocRefLink } from '../../../components/DocRefLink';
import { EditarFacturaRawDialog } from './EditarFacturaRawDialog';

export function DetalleConciliacion({ item, onAccion, onRefresh }) {
  const { user } = useAuth();
  const [accionLoading, setAccionLoading] = useState(false);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [nota, setNota] = useState('');
  const [editRow, setEditRow] = useState(null);
  // Iter 308.d / 331 — match perfectos visibles por defecto (toggle marcado)
  const [mostrarTodos, setMostrarTodos] = useState(true);
  // Iter 331 — "Ya pagadas" ocultas por defecto (toggle desmarcado)
  const [mostrarPagadas, setMostrarPagadas] = useState(false);
  const canEditRaw = (user?.rol === 'admin' || user?.rol === 'gerencia') && item?.estado !== 'superada';

  if (!item) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <FileSearch className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Seleccioná una conciliación de la bandeja para ver el detalle.</p>
        </CardContent>
      </Card>
    );
  }

  const handleAccion = async (accion) => {
    if (accionLoading) return;
    setAccionLoading(true);
    try {
      await conciliacionProveedoresApi.aprobar(item.id, accion, nota || '');
      toast.success(`Conciliación ${accion === 'aprobada' ? 'aprobada' : 'rechazada'}`);
      setNota('');
      onAccion?.(accion);
      onRefresh?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setAccionLoading(false); }
  };

  const handleRematch = async () => {
    if (rematchLoading) return;
    setRematchLoading(true);
    try {
      const res = await conciliacionProveedoresApi.rematch(item.id);
      const t = res.data?.conciliacion?.totales || {};
      const msg = `✓ Rematch: ${t.match_perfecto || 0} OK · ${t.match_parcial || 0} parcial · ${t.en_rcv_pendiente_contab || 0} en RCV · ${t.faltante || 0} faltante · ${t.ya_pagada || 0} ya pagada`;
      toast.success(res.data?.desbloqueo_automatico ? `${msg} — Proveedor desbloqueado automáticamente` : msg);
      onRefresh?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setRematchLoading(false); }
  };

  const matching = item.matching || [];
  const totales = item.totales || {};
  const semaforo = getSemaforo(item);

  // Iter 331 — filtros de visibilidad:
  //  * match_perfecto → visible si `mostrarTodos` (marcado por defecto)
  //  * ya_pagada → visible sólo si `mostrarPagadas` (desmarcado por defecto)
  //  * resto (faltante / parcial / en RCV) → siempre visibles
  const matchingVisible = matching.filter((m) => {
    if (m.estado === 'ya_pagada') return mostrarPagadas;
    if (m.estado === 'match_perfecto') return mostrarTodos;
    return true;
  });
  const ocultos = matching.filter((m) => m.estado === 'match_perfecto' && !mostrarTodos).length;
  const pagadasOcultas = matching.filter((m) => m.estado === 'ya_pagada' && !mostrarPagadas).length;

  // Placeholder de correo multi-proveedor reenviado a corporativo (iter 258)
  if (item.estado === 'reenviado_corporativo') {
    const provs = item.proveedores_detectados || [];
    return (
      <div className="space-y-4" data-testid="detalle-reenviado-corporativo">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="w-4 h-4 shrink-0 text-slate-500" />
              Reenviado a corporativo — pendiente de separar
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1">{item.filename}</div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="p-2.5 bg-slate-50 border border-slate-300 rounded text-xs text-slate-700">
              Este estado de cuenta llegó con <strong>{provs.length}</strong> proveedores y se
              reenvió a <strong>{item.reenviado_a}</strong> para separación manual. Cuando reenvíen
              cada estado de cuenta por separado a <strong>concilia@</strong>, se conciliarán
              automáticamente.
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Proveedores detectados:</div>
              <ul className="text-xs space-y-1">
                {provs.map((p, i) => (
                  <li key={i} className="flex items-center gap-2" data-testid={`reenviado-prov-${i}`}>
                    <span className="font-mono text-[10px] bg-slate-100 px-1 rounded">{p.proveedor_rut || '—'}</span>
                    <span>{p.proveedor_nombre}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {item.asunto && <div>Asunto: {item.asunto}</div>}
              <div>Reenviado el {fmtDate(item.reenviado_corporativo_at)} · desde {item.email_from || '—'}</div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => handleAccion('aprobada')}
                disabled={accionLoading} data-testid="btn-resolver-reenviado">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como resuelto
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header detalle */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base flex items-center gap-2 truncate">
                <FileText className="w-4 h-4 shrink-0" />
                {item.proveedor_nombre || '(proveedor sin identificar)'}
              </CardTitle>
              <div className="text-xs text-muted-foreground mt-1">
                {item.proveedor_rut || '(sin RUT)'} · {item.filename}
              </div>
            </div>
            <SemaforoBadge color={semaforo} />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-5 gap-2 text-sm">
            <div className="p-2 bg-emerald-50 border border-emerald-200 rounded">
              <div className="text-[11px] text-emerald-700">Match perfecto</div>
              <div className="font-semibold text-emerald-800">{totales.match_perfecto || 0}</div>
            </div>
            <div className="p-2 bg-amber-50 border border-amber-200 rounded">
              <div className="text-[11px] text-amber-700">Parcial</div>
              <div className="font-semibold text-amber-800">{totales.match_parcial || 0}</div>
            </div>
            <div className="p-2 bg-indigo-50 border border-indigo-200 rounded" title="Factura existe en RCV/SII pero aún no se contabilizó en Alegra">
              <div className="text-[11px] text-indigo-700">En RCV s/contab.</div>
              <div className="font-semibold text-indigo-800">{totales.en_rcv_pendiente_contab || 0}</div>
            </div>
            <div className="p-2 bg-red-50 border border-red-200 rounded">
              <div className="text-[11px] text-red-700">Faltante</div>
              <div className="font-semibold text-red-800">{totales.faltante || 0}</div>
            </div>
            <div className="p-2 bg-slate-50 border border-slate-200 rounded">
              <div className="text-[11px] text-slate-700">Ya pagada</div>
              <div className="font-semibold text-slate-800">{totales.ya_pagada || 0}</div>
            </div>
          </div>
          {(item.tiene_faltantes_anteriores || item.tiene_faltantes_antiguos) && (
            <div className="mt-3 space-y-2">
              {item.tiene_faltantes_anteriores && (
                <div className="p-2.5 bg-red-50 border border-red-300 rounded flex items-start gap-2 text-xs">
                  <ShieldAlert className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-red-800">🔴 Bloqueo de PAGO activado</div>
                    <div className="text-red-700">Hay factura(s) faltante(s) con fecha anterior a la que pretendo pagar. El proveedor queda bloqueado para liquidar en Flujo de Caja.</div>
                  </div>
                </div>
              )}
              {item.tiene_faltantes_antiguos && (
                <div className="p-2.5 bg-orange-50 border border-orange-300 rounded flex items-start gap-2 text-xs">
                  <ShieldAlert className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-orange-800">🟠 Bloqueo de OC activado</div>
                    <div className="text-orange-700">
                      {(item.faltantes_antiguos_detalle?.length || 0)} factura(s) con &gt;{item.dias_umbral_antiguos || 30} días sin contabilizar.
                      Compras NO puede emitir nuevas OC a este proveedor hasta regularizar.
                    </div>
                    {item.faltantes_antiguos_detalle?.length > 0 && (
                      <div className="mt-1 text-[10px] text-orange-600">
                        Ej: {item.faltantes_antiguos_detalle.slice(0, 3).map((f) => `${f.folio || 's/folio'} (${f.dias_antiguedad}d)`).join(', ')}
                        {item.faltantes_antiguos_detalle.length > 3 && ` +${item.faltantes_antiguos_detalle.length - 3} más`}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-3 text-[11px] text-muted-foreground flex items-center justify-between gap-2 flex-wrap">
            <span>
              Subido por <strong>{item.uploaded_by || '—'}</strong> · {fmtDate(item.uploaded_at)}
              {item.rematched_at && (
                <span className="ml-2 text-emerald-700">· Último rematch: {fmtDate(item.rematched_at)}</span>
              )}
            </span>
            <Button size="sm" variant="outline" onClick={handleRematch} disabled={rematchLoading} data-testid="btn-rematch-conciliacion" className="h-7 text-xs">
              <Zap className="w-3.5 h-3.5 mr-1" />
              {rematchLoading ? 'Refrescando…' : 'Refrescar matching (Alegra)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla side-by-side */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" /> Comparación factura por factura
            </CardTitle>
            {/* Iter 331 — toggles: match perfectos (marcado por defecto) y ya pagadas (desmarcado) */}
            {matching.length > 0 && (
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="toggle-mostrar-todos">
                  <input
                    type="checkbox"
                    checked={mostrarTodos}
                    onChange={(e) => setMostrarTodos(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Mostrar también los match perfectos
                  {!mostrarTodos && ocultos > 0 && (
                    <span className="ml-1 text-emerald-700 font-medium">({ocultos} ocultos)</span>
                  )}
                </label>
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="toggle-mostrar-pagadas">
                  <input
                    type="checkbox"
                    checked={mostrarPagadas}
                    onChange={(e) => setMostrarPagadas(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Mostrar las ya pagadas
                  {!mostrarPagadas && pagadasOcultas > 0 && (
                    <span className="ml-1 text-slate-600 font-medium">({pagadasOcultas} ocultas)</span>
                  )}
                </label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th colSpan={4} className="text-left px-2 py-1.5 border-b border-r">📄 ESTADO DE CUENTA (Proveedor)</th>
                  <th colSpan={3} className="text-left px-2 py-1.5 border-b">📒 ALEGRA (Sistema)</th>
                  <th className="text-left px-2 py-1.5 border-b">Estado</th>
                </tr>
                <tr className="text-[11px] text-muted-foreground bg-muted/20">
                  <th className="text-left px-2 py-1 border-b">Folio</th>
                  <th className="text-left px-2 py-1 border-b">Fecha</th>
                  <th className="text-right px-2 py-1 border-b">Monto</th>
                  <th className="text-right px-2 py-1 border-b border-r">Saldo</th>
                  <th className="text-left px-2 py-1 border-b">Folio</th>
                  <th className="text-right px-2 py-1 border-b">Total</th>
                  <th className="text-right px-2 py-1 border-b">Saldo</th>
                  <th className="text-left px-2 py-1 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {matching.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Sin facturas</td></tr>
                )}
                {matching.length > 0 && matchingVisible.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-6 text-emerald-700">
                    {pagadasOcultas > 0
                      ? `✓ Nada por gestionar. Hay ${pagadasOcultas} factura(s) ya pagada(s) oculta(s) — marcá "Mostrar las ya pagadas" para verlas.`
                      : '✓ Todas las facturas coinciden perfectamente con Alegra. Nada por gestionar.'}
                  </td></tr>
                )}
                {matchingVisible.map((m, idx) => {
                  const bill = m.alegra_bill;
                  const rowCls = m.estado === 'faltante' ? 'bg-red-50/50'
                               : m.estado === 'match_parcial' ? 'bg-amber-50/40'
                               : m.estado === 'ya_pagada' ? 'bg-slate-50/40' : '';
                  return (
                    <tr key={idx} className={`${rowCls} border-b hover:bg-muted/30`}>
                      <td className="px-2 py-1.5 font-mono">
                        {m.folio_proveedor
                          ? <DocRefLink tipo="dte" folio={m.folio_proveedor} rut={item.proveedor_rut} label={m.folio_proveedor} testId={`concil-folio-prov-${idx}`} />
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5">{fmtFecha(m.fecha_proveedor)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtCLP(m.monto_proveedor)}</td>
                      <td className="px-2 py-1.5 text-right border-r">{fmtCLP(m.saldo_proveedor)}</td>
                      <td className="px-2 py-1.5 font-mono">
                        {bill?.folio
                          ? <DocRefLink tipo="dte" folio={bill.folio} rut={item.proveedor_rut} label={bill.folio} testId={`concil-folio-alegra-${idx}`} />
                          : <span className="text-red-600">FALTA</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">{bill ? fmtCLP(bill.total) : '—'}</td>
                      <td className="px-2 py-1.5 text-right">{bill ? fmtCLP(bill.saldo) : '—'}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild><span><EstadoMatchBadge estado={m.estado} /></span></TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <div className="text-xs">{m.observacion || '—'}</div>
                                {m.diff_monto > 0 && <div className="text-xs mt-1">Δ Monto: {fmtCLP(m.diff_monto)}</div>}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {canEditRaw && (
                            <button
                              type="button"
                              className="p-0.5 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                              title="Corregir dato del estado de cuenta (admin/gerencia)"
                              data-testid={`btn-editar-raw-${idx}`}
                              onClick={() => setEditRow({
                                index: idx,
                                folio_original: m.folio_proveedor || '',
                                folio: m.folio_proveedor || '',
                                monto_total: m.monto_proveedor ?? '',
                                saldo_pendiente: m.saldo_proveedor ?? '',
                                fecha_emision: m.fecha_proveedor || '',
                              })}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(item.ediciones_raw?.length > 0) && (
            <div className="px-3 py-2 border-t bg-amber-50/40 text-[11px] text-amber-800 space-y-0.5" data-testid="historial-ediciones-raw">
              <div className="font-medium flex items-center gap-1"><Pencil className="w-3 h-3" /> Correcciones manuales ({item.ediciones_raw.length})</div>
              {item.ediciones_raw.slice(-5).map((ed, i) => (
                <div key={i} className="text-amber-700">
                  {Object.entries(ed.valores_nuevos || {}).map(([k, v]) =>
                    `${k}: ${ed.valores_previos?.[k] ?? '—'} → ${v}`,
                  ).join(' · ')}
                  {' — '}{ed.editado_por} el {fmtDate(ed.editado_at)}
                  {ed.motivo && ` · "${ed.motivo}"`}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editRow && (
        <EditarFacturaRawDialog
          conciliacionId={item.id}
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => onRefresh?.()}
        />
      )}
      {item.estado === 'pendiente_revision' && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <Textarea placeholder="Nota de revisión (opcional, ej: 'falsa alarma — factura pagada el día 15 con cheque XYZ')"
              value={nota} onChange={(e) => setNota(e.target.value)} rows={2} className="text-sm"
              data-testid="textarea-nota-revision"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => handleAccion('rechazada')} disabled={accionLoading} data-testid="btn-rechazar-conciliacion">
                <XCircle className="w-4 h-4 mr-1" /> Rechazar
              </Button>
              <Button size="sm" onClick={() => handleAccion('aprobada')} disabled={accionLoading} data-testid="btn-aprobar-conciliacion">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {item.estado === 'resuelta' && (
        <div className="text-xs text-slate-700 px-3 py-2 bg-slate-50 border border-slate-300 rounded">
          <div className="font-medium">🔒 Resuelta automáticamente el {fmtDate(item.resuelta_at)}</div>
          {item.resuelta_motivo && <div className="mt-1">Motivo: {item.resuelta_motivo}</div>}
          {(item.facturas_resueltas_snapshot?.length || 0) > 0 && (
            <div className="mt-1.5 text-[11px]">
              Fechas de pago (Alegra):
              <ul className="ml-4 mt-0.5 list-disc">
                {item.facturas_resueltas_snapshot.map((f, i) => (
                  <li key={i}>Folio {f.folio} · pagada el {f.fecha_pago_alegra || '—'}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {item.estado === 'aprobada' && (
        <div className="text-xs text-emerald-700 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded">
          {item.auto_aprobada
            ? <>✓ Aprobada automáticamente por el sistema el {fmtDate(item.auto_aprobada_at || item.aprobada_at)}
                {item.auto_aprobada_motivo && <div className="mt-1">Motivo: {item.auto_aprobada_motivo}</div>}
              </>
            : <>✓ Aprobada por {item.revisada_por} el {fmtDate(item.revisada_at)}
                {item.revision_nota && <div className="mt-1">Nota: {item.revision_nota}</div>}
              </>}
        </div>
      )}
      {item.estado === 'rechazada' && (
        <div className="text-xs text-red-700 px-2 py-1 bg-red-50 border border-red-200 rounded">
          ✗ Rechazada por {item.revisada_por} el {fmtDate(item.revisada_at)}
          {item.revision_nota && <div className="mt-1">Nota: {item.revision_nota}</div>}
        </div>
      )}
      {item.estado === 'superada' && (
        <div className="text-xs text-slate-700 px-2 py-1 bg-slate-50 border border-slate-200 rounded">
          ⟲ Superada el {fmtDate(item.superada_at)} — fue reemplazada por una conciliación más reciente del mismo proveedor.
          {item.superada_por && <div className="mt-1 font-mono text-[10px] opacity-70">ref: {item.superada_por}</div>}
        </div>
      )}
    </div>
  );
}
